import { useState, useEffect } from 'react';

import {
    AppBar,
    Box,
    Button,
    Collapse,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControl,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ReportIcon from '@mui/icons-material/Report';

import MemberHeader from './MemberHeader/MemberHeader';
import {
    fetchEthnicities,
    fetchPreferredPronouns,
    fetchRaces,
    getMemberByMemberId,
    setClosedStatusModalIsOpen,
    setMemberAccessMessage,
    setMemberMenuIsOpen,
    setShowDetails,
    setShowMemberAccess,
    fetchPreviousChartsByMemberId,
    fetchMemberNotesByMemberId,
    setShowNotes,
} from '../../store/memberDemographicsDetails.slice';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { RootState } from '../../reducers';
import { fetchHealthPlans, fetchInsuranceTypes, fetchMemberDemoGraphicInfo, fetchMemberHealthPlans, logMemberAccess } from '../../store/memberDetail.slice';

import WarningIcon from '@mui/icons-material/Warning';
import MemberInformation from './MemberInformation/MemberInformation';

import Authorization from '../../utils/Authorization';

import { MemberStatusEnum } from '../../Enum/MemberStatusEnum';

import { insertTimeline } from '../../store/timeline.slice';
import { TimelineTypeEnum } from '../../Enum/TimelineTypeEnum';
import Loading from '../Shared/Loading';

import { useLocation, useParams, Outlet } from 'react-router';
import { Link } from 'react-router-dom';

const drawerWidth = 225;

const MemberAccessDialog = (props) => {
    const { closeMemberAccessHandler, message, messageIsValid, setMessage, showMemberAccess, submitMemberAccessHandler } = props;
    const dispatch = useAppDispatch();

    return (
        <Dialog open={showMemberAccess} onClose={closeMemberAccessHandler} fullWidth maxWidth="sm">
            <DialogTitle>Member Access</DialogTitle>
            <DialogContent>
                <Typography variant="body1">Please provide your reason for accessing this member record.</Typography>
                <FormControl fullWidth={true}>
                    <TextField
                        fullWidth
                        error={!messageIsValid}
                        multiline
                        required
                        label="Reason"
                        sx={{ marginY: 2 }}
                        helperText={!messageIsValid ? 'Please enter a reason for accessing this member record.' : ''}
                        variant="outlined"
                        rows={6}
                        value={message}
                        name="message"
                        onChange={(e) => dispatch(setMessage(e.target.value))}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button className="button-120" variant="outlined" color="primary" onClick={closeMemberAccessHandler}>
                    Cancel
                </Button>
                <Button className="button-120" variant="contained" color="primary" onClick={submitMemberAccessHandler}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ClosedStatusConfirmationDialog = (props) => {
    const { chartDetails, onClose, open, onConfirm } = props;
    const clonedHRA = chartDetails?.Status === MemberStatusEnum.HRAInReview && chartDetails?.HasCloned;
    const clonedFUHFUMINPROGRESS = chartDetails?.Status === MemberStatusEnum.FUHFUMInProgress && chartDetails?.HasCloned;
    const clonedTrueBalance = chartDetails?.Status === MemberStatusEnum.AndNextInProgress && chartDetails?.HasCloned;
    const status = clonedHRA
        ? 'HRA In Review and an Active record'
        : clonedFUHFUMINPROGRESS
        ? 'FUH/FUM In Progress and an Active record'
        : clonedTrueBalance
        ? 'andNext In Progress and an Active record'
        : 'Closed';

    const handleClose = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        onClose();
    };

    const cancelConfirmation = () => {
        window.close();
    };
    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title" sx={{ backgroundColor: 'error.main' }} color={'white'}>
                Confirmation
            </DialogTitle>
            <DialogContent>
                <Typography id="alert-dialog-description" p={2} marginTop={2}>
                    This record status is <b>{status}</b>. Are you sure you want to continue?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button className="button-120" variant="outlined" color="error" onClick={cancelConfirmation}>
                    No
                </Button>
                <Button className="button-120" variant="contained" color="error" onClick={onConfirm}>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const MemberDemographicsDetails = (props) => {
    //
    const params = useParams<any>();
    const location = useLocation();
    const lastPathSegment = location.pathname.split('/').pop();
    const path = lastPathSegment;

    const memberId = props.memberId || params.memberId;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const providerNavbarHeight = document.getElementById('provider-navbar')?.offsetHeight;
    const memberHeaderHeight = document.getElementById('member-header')?.offsetHeight;
    const top = providerNavbarHeight + memberHeaderHeight;
    const tabsHeight = top ? `calc(100vh - ${top}px)` : 'calc(100vh - 140px)';

    const auth = new Authorization();
    const {
        chartDetails,
        closedStatusModalIsOpen,
        hasCriticalNotes,

        memberAccessMessage,
        memberMenuIsOpen,
        showDetails,
        showMemberAccess,
    } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const dispatch = useAppDispatch();
    const safetyPlanNeeded = chartDetails?.SafetyPlanNeeded;
    const planNeeded = safetyPlanNeeded ? 'Safety Plan Recommended' : '';
    const planIcon = (active) => {
        return (
            <Tooltip title={planNeeded} placement="right">
                <ReportIcon sx={{ color: active ? 'white' : 'error.main', marginLeft: '8px' }} />
            </Tooltip>
        );
    };

    const [messageIsValid, setMessageIsValid] = useState(true);

    const openMemberAccessHandler = () => {
        setMessageIsValid(true);
        dispatch(setShowMemberAccess(true));
    };

    const closeMemberAccessHandler = () => {
        dispatch(setMemberAccessMessage(''));
        dispatch(setShowMemberAccess(false));
    };

    const submitMemberAccessHandler = () => {
        if (memberAccessMessage.length === 0) {
            setMessageIsValid(false);
            return;
        } else {
            setMessageIsValid(true);
            dispatch(insertTimeline(memberId, memberAccessMessage, TimelineTypeEnum.ClosedMemberAccessed));
            closeMemberAccessHandler();
            dispatch(setClosedStatusModalIsOpen(false));
        }
    };

    const collapseMenuHandler = () => {
        dispatch(setMemberMenuIsOpen(!memberMenuIsOpen));
    };

    const memberDetailPages = [
        { name: 'Performance', isVisible: true, path: 'performance' },
        {
            name: 'Assessments',
            isVisible: auth.isInPermission('View member assessment') || auth.isInPermission('Edit member assessment data'),
            path: 'assessment-dashboard',
        },
        {
            name: 'Integrated Care Plan',
            isVisible: true,
            path: 'integrated-care-plan',
            icon: planNeeded?.length > 0 ? planIcon(path === 'integrated-care-plan') : null,
        },
        {
            name: 'Scheduler',
            isVisible: auth.isInPermission('View member appointment') || auth.isInPermission('Add / Edit member appointment'),
            path: 'scheduling',
        },
        {
            name: 'Documents',
            isVisible: auth.isInPermission('View member documents') || auth.isInPermission('Manage member document'),
            path: 'member-documents',
        },
        { name: 'Timeline', isVisible: auth.isInPermission('View member Timeline'), path: 'timeline' },
        {
            name: 'Engagement',
            isVisible: auth.isInPermission('View engagements') || auth.isInPermission('Add / Edit engagements'),
            path: 'document-engagement',
        },
        {
            name: 'Education',
            isVisible: auth.isInPermission('View member education materials') || auth.isInPermission('Assign education materials to member'),
            path: 'education',
        },
        { name: 'Vital Signs', isVisible: auth.isInPermission('View member vital signs'), path: 'vital-signs' },
        { name: 'Requests', isVisible: auth.isInPermission('View the requests'), path: 'member-requests' },
        {
            name: 'Medication Management',
            isVisible: auth.isInPermission('View member assessment') || auth.isInPermission('Edit member assessment data'),
            path: 'medication-management',
        },
        { name: 'Clinical Data', isVisible: auth.isInPermission('Edit member clinical data'), path: 'clinical-data' },
        { name: 'Transitional Care', isVisible: true, path: 'transitional-care' },
        { name: 'HIE', isVisible: true, path: 'hie' },
        { name: 'Budget Form', isVisible: true, path: 'budget-form' },
        { name: 'AI Chat', isVisible: true, path: 'member-chat' },
        {
            name: 'Onboarding',
            isVisible: auth.isInPermission('View engagements') || auth.isInPermission('Add / Edit engagements'),
            path: 'onboarding-engagement',
        },
    ];

    useEffect(() => {
        document.title = props.title;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(getMemberByMemberId(memberId));
        dispatch(fetchMemberDemoGraphicInfo(memberId));
        dispatch(fetchHealthPlans());
        dispatch(fetchInsuranceTypes());
        dispatch(fetchPreferredPronouns());
        dispatch(fetchEthnicities());
        dispatch(fetchRaces());
        dispatch(fetchMemberHealthPlans(memberId));
        dispatch(fetchPreviousChartsByMemberId(memberId));
        dispatch(fetchMemberNotesByMemberId(memberId));
        dispatch(logMemberAccess(memberId));
    }, [dispatch, memberId]);

    return (
        <>
            <Box sx={{ display: 'flex', marginTop: `${memberHeaderHeight}px` }}>
                {isLoading && <Loading message="Loading..." />}
                <ClosedStatusConfirmationDialog
                    chartDetails={chartDetails}
                    open={closedStatusModalIsOpen}
                    onClose={() => {
                        dispatch(setClosedStatusModalIsOpen(false));
                    }}
                    onConfirm={openMemberAccessHandler}
                />
                <MemberAccessDialog
                    closeMemberAccessHandler={closeMemberAccessHandler}
                    message={memberAccessMessage}
                    messageIsValid={messageIsValid}
                    setMessage={setMemberAccessMessage}
                    showMemberAccess={showMemberAccess}
                    submitMemberAccessHandler={submitMemberAccessHandler}
                />
                <CssBaseline />
                <AppBar
                    id="member-header"
                    position="fixed"
                    sx={{ backgroundColor: 'white', color: 'rgba(0,0,0,0.87)', height: '64px', top: providerNavbarHeight }}
                >
                    <Toolbar>
                        <Stack sx={{ width: '100%' }}>
                            <MemberHeader
                                memberId={memberId}
                                chartDetails={chartDetails}
                                showDetails={false}
                                hideDetailsHandler={() => {
                                    dispatch(setShowDetails(!showDetails));
                                }}
                            />
                        </Stack>
                    </Toolbar>
                    {hasCriticalNotes ? (
                        <Stack
                            direction="row"
                            width={'100%'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            sx={{ background: '#ed6c02', cursor: 'pointer', height: '36px' }}
                            spacing={2}
                            p={1}
                            onClick={() => dispatch(setShowNotes(true))}
                        >
                            <WarningIcon sx={{ fontSize: '1.5rem', color: 'white' }} />
                            <Typography variant="body1" color="white">
                                Warning - Please read before interacting with Member
                            </Typography>
                        </Stack>
                    ) : null}
                </AppBar>
                <Drawer
                    id="member-details-drawer"
                    sx={{
                        width: memberMenuIsOpen ? (isMobile ? 0 : drawerWidth) : isMobile ? 0 : 28,
                        '& .MuiDrawer-paper': {
                            width: memberMenuIsOpen ? (isMobile ? 0 : drawerWidth) : isMobile ? 0 : 28,
                            boxSizing: 'border-box',
                            top: isMobile ? providerNavbarHeight : top,
                            zIndex: isMobile ? 1300 : 100,
                            marginTop: hasCriticalNotes && !isMobile ? '28px' : '',
                        },
                    }}
                    variant={'permanent'}
                    anchor="left"
                    open={memberMenuIsOpen}
                    onClose={() => dispatch(setMemberMenuIsOpen(false))}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    <Box sx={{ pl: isMobile ? '75px' : 0, display: 'flex', position: 'relative' }}>
                        {(() => {
                            const tabs = memberDetailPages.filter((p) => p.isVisible).sort((a, b) => a.name.localeCompare(b.name));

                            let activeIndex = tabs.findIndex(
                                (t) => t.path === path || (t.path === 'assessment-dashboard' && location.pathname.includes('/assessment-dashboard'))
                            );
                            if (path === 'member-profile') {
                                activeIndex = tabs.findIndex((t) => t.path === 'member-profile');
                            }

                            return (
                                <Tabs
                                    orientation="vertical"
                                    variant="scrollable"
                                    value={activeIndex + 1}
                                    sx={{
                                        width: drawerWidth,
                                        height: tabsHeight,
                                        '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left' },
                                        '& .Mui-selected': { color: 'white', backgroundColor: 'primary.main' },
                                    }}
                                >
                                    <Tab label="Member Profile" component={Link} to={`member-profile`} value={0} />
                                    {tabs.map((page, idx) => {
                                        // handle Assessments special case
                                        if (page.name === 'Assessments' && page.isVisible) {
                                            return <Tab key={`tab_${idx}`} label={page.name} component={Link} to={`assessment-dashboard`} value={idx + 1} />;
                                        }

                                        return (
                                            <Tab
                                                key={`tab_${idx}`}
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <span>{page.name}</span>
                                                        {page.icon ? page.icon : null}
                                                    </Box>
                                                }
                                                component={Link}
                                                to={`${page.path}`}
                                                value={idx + 1}
                                            />
                                        );
                                    })}
                                </Tabs>
                            );
                        })()}
                        <Button
                            sx={{
                                p: 0,
                                m: 0,
                                borderRadius: 0,
                                minWidth: '28px',
                                height: `calc(100vh - ${top}px)`,
                                color: 'text.primary',
                                backgroundColor: 'rgb(230,230,230)',
                                position: memberMenuIsOpen ? 'sticky' : 'static',
                                top: top,
                                left: 'auto',
                                zIndex: 'auto',
                                '&:hover': { backgroundColor: 'rgb(150, 150, 150)', color: 'rgb(255,255,255)' },
                                display: isMobile ? 'none' : 'block',
                            }}
                            onClick={collapseMenuHandler}
                        >
                            {memberMenuIsOpen ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
                        </Button>
                    </Box>
                </Drawer>

                <Box
                    component="main"
                    sx={{
                        marginTop: hasCriticalNotes ? '28px' : '',
                        marginLeft: isMobile && memberMenuIsOpen ? 0 : 'auto',
                        marginRight: 'auto',
                        width: isMobile ? '-webkit-fill-available' : '100%',
                        minWidth: 0,
                        flex: '1 1 0%',
                        p: 0,
                    }}
                >
                    <Collapse
                        in={showDetails}
                        sx={{
                            marginX: 0,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            '& > .MuiCollapse-wrapper, & > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner': {
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                            },
                        }}
                    >
                        <MemberInformation chartDetails={chartDetails} memberId={memberId} />
                    </Collapse>
                    {<Outlet />}
                </Box>
            </Box>
        </>
    );
};

export default MemberDemographicsDetails;
